import React, { useEffect, useState } from 'react';
import { AnimateSharedLayout } from 'framer-motion';
import { gql, useApolloClient, useFragment } from '@apollo/client';
import { MemoryRouter } from 'react-router';
import { Funnel, Step, Process } from '@domain-group/fe-funnel-utils';
import { Loader } from '@domain-group/fe-blueprint-ui/loader';
import { DriverType, storageFactory } from '@domain-group/fe-helper';
import { useInView } from 'react-intersection-observer';
import funnelReducer from './utils/funnel-reducer';
import { sendEnquiry, Details, Intent, Success } from './components/funnel';
import { ContactEnquiryFormFragment } from '../../generated/graphql';
import { OverallStarRating } from '../shared/recommendations/types';

const TYPE_NAME = 'Contact';
const FRAGMENT_NAME = 'ContactEnquiryForm';
const FRAGMENT = gql`
  fragment ContactEnquiryForm on Contact {
    __typename
    id
    name
    hasEmail
    profilePhoto {
      url
    }
  }
`;

export type FunnelState = {
  intent: string;
  details: unknown;
  submissionError: unknown;
};

export type FunnelProps = {
  onError: () => void;
  contact: unknown;
  contactId: number;
  apolloClient: unknown;
  isShortForm: boolean;
  onSelectIntent?: () => void;
  onSubmitIntent?: () => void;
  onSubmit: ({
    intent,
    phone,
    postcode,
    message,
  }: {
    intent: string;
    phone: string;
    postcode: string;
    message: string;
  }) => void;
  onSubmitSuccess: ({ intent }: { intent: string }) => void;
  onSubmitError: () => void;
  onFinish: (() => void) | undefined;
};

export type Props = {
  agencyId: number;
  agentIdV2: string;
  contactId: number;
  isShortForm?: boolean;
  overallStarRating?: OverallStarRating;
  onInView?: () => void;
  onSelectIntent?: (intent: string) => void;
  onSubmitIntent?: (intent: string) => void;
  onSubmit?: ({
    intent,
    phone,
    postcode,
    message,
  }: {
    intent: string;
    phone: string;
    postcode: string;
    message: string;
  }) => void;
  onSubmitSuccess: ({ intent }: { intent: string }) => void;
  onSubmitError?: () => void;
  onFinish?: (() => void) | undefined;
};

const ContactEnquiryFormWithFragment = ({
  agencyId,
  agentIdV2,
  contactId,
  overallStarRating,
  isShortForm = false,
  onInView = () => null,
  onSelectIntent = () => null,
  onSubmitIntent = () => null,
  onSubmit = () => null,
  onSubmitSuccess = () => null,
  onSubmitError = () => null,
  onFinish = undefined,
}: Props): JSX.Element | null => {
  const [toggleSeen, setToggleSeen] = useState(false);
  const { ref, inView } = useInView({
    // This means it will fire when 75% of the component is in view
    threshold: 0.75,
    // triggerOnce: true,
  });

  useEffect(() => {
    if (inView && !toggleSeen) {
      onInView();
      setToggleSeen(true);
    }
  }, [inView, toggleSeen, onInView]);

  const storage = storageFactory({
    name: `${Math.random()}-contact-agents-form`,
    driver: DriverType.SESSIONSTORAGE,
  });

  const client = useApolloClient();
  const { complete, data: contact } = useFragment<
    ContactEnquiryFormFragment,
    unknown
  >({
    from: {
      __typename: TYPE_NAME,
      id: `${agencyId}-${agentIdV2}`,
    },
    fragment: FRAGMENT,
    fragmentName: FRAGMENT_NAME,
  });

  if (!complete || !contact?.id || !contact?.hasEmail) {
    return null;
  }

  return (
    <section ref={ref} data-testid="agent-enquiry-form">
      <AnimateSharedLayout>
        <MemoryRouter>
          <Funnel
            reducer={funnelReducer}
            firstStep={isShortForm ? 'details' : 'intent'}
            storage={storage}
            contact={contact}
            contactId={contactId}
            apolloClient={client}
            isShortForm={isShortForm}
            overallStarRating={
              overallStarRating
                ? {
                    rating: overallStarRating.rating,
                    totalReviews: overallStarRating.totalReviews,
                  }
                : undefined
            }
            onSelectIntent={onSelectIntent}
            onSubmitIntent={onSubmitIntent}
            onSubmit={onSubmit}
            onSubmitSuccess={onSubmitSuccess}
            onSubmitError={onSubmitError}
            onFinish={onFinish}
          >
            <Step name="intent" component={Intent} nextStep="details" />
            <Step
              name="details"
              component={Details}
              nextStep={(funnelState: FunnelState) =>
                funnelState.details ? 'send' : 'intent'
              }
            />
            <Process
              name="send"
              sideEffect={sendEnquiry}
              loadingIndicator={Loader}
              nextStep={(funnelState: FunnelState) =>
                funnelState.submissionError ? 'details' : 'success'
              }
            />
            <Step name="success" component={Success} nextStep="intent" />
          </Funnel>
        </MemoryRouter>
      </AnimateSharedLayout>
    </section>
  );
};

ContactEnquiryFormWithFragment.fragmentName = FRAGMENT_NAME;
ContactEnquiryFormWithFragment.fragment = FRAGMENT;

export default ContactEnquiryFormWithFragment;
